import { createContext,createElement,useContext,useEffect,useRef,type ReactNode } from 'react'
import { apiGet, useQuery } from './hooks'
import {useVisibleRefresh} from './useVisibleRefresh'
import type { Envelope, FolderSummary } from './types'

function useFolderQuery(allowed: boolean) {
  const controller=useRef<AbortController|null>(null)
  const query = useQuery<Envelope<FolderSummary[]>>(async () => {
    if (!allowed) return { trace_id: '-', data: [] }
    controller.current?.abort();const active=new AbortController();controller.current=active
    const rows: FolderSummary[] = [], seen = new Set<string>()
    let cursor: string | undefined
    do {
      const page = await apiGet<Envelope<FolderSummary[]>>('/folders', { limit: 200, cursor },{signal:active.signal,timeoutMs:12000})
      rows.push(...page.data)
      cursor = page.next_cursor || undefined
      if (cursor && seen.has(cursor)) throw new Error('目录分页异常，请刷新后重试')
      if (cursor) seen.add(cursor)
    } while (cursor)
    return { trace_id: '-', data: rows }
  }, [allowed])
  useVisibleRefresh(allowed,query.refresh,60000,false)
  useEffect(() => {
    if(!allowed)return
    const reload = () => { void query.reload() }
    const permissions=()=>{controller.current?.abort();query.commitData(null);void query.reload()}
    window.addEventListener('kb:folders-changed', reload)
    window.addEventListener('kb:permissions-changed', permissions)
    return () => {window.removeEventListener('kb:folders-changed', reload);window.removeEventListener('kb:permissions-changed', permissions);controller.current?.abort()}
  }, [allowed,query.reload,query.commitData])
  return query
}

const FoldersContext=createContext<ReturnType<typeof useFolderQuery>|null>(null)
/** The mounted shell owns one current directory query; no cross-session storage. */
export function LibraryFoldersProvider({allowed,children}:{allowed:boolean;children:ReactNode}){
  const query=useFolderQuery(allowed)
  return createElement(FoldersContext.Provider,{value:query},children)
}
export function useLibraryFolders(allowed:boolean){
  const shared=useContext(FoldersContext)
  const fallback=useFolderQuery(allowed&&!shared)
  return allowed&&shared?shared:fallback
}
